import {
    Component,
    ElementRef,
    inject,
    Input,
    signal,
    computed
} from "@angular/core";
import {
    CompanyEntity,
    PointEntity,
    VisualizationEntity,
    SerieEntity
} from "@arch/domain/entity";
import { DateUtils, FormatNumbers } from "@arch/_shared/utils";
import { ColorsService } from "@arch/domain/application/services/colors.service";
import { SharedMaterialModule } from "@app/shared/modules/shared-material.module";
import { VisualizationOptionsType } from "@arch/_shared/types/options-visualization";
import { DeviceService } from "@shared/services/device.service";
import { GetVisualizationDataController } from "@arch/presentation/controllers";
@Component({
    selector: "app-table-values",
    imports: [SharedMaterialModule],
    templateUrl: "./table-values.component.html",
    styleUrl: "./table-values.component.scss"
})
export class TableValuesComponent {
    @Input() set options(value: VisualizationOptionsType) {
        this.optionsSignal.set(value);
    }
    get options() {
        return this.optionsSignal();
    }
    private readonly optionsSignal = signal<VisualizationOptionsType>(null);

    @Input() company: CompanyEntity;
    @Input() visualization: VisualizationEntity;

    private readonly elementRef = inject(ElementRef);
    private readonly deviceService = inject(DeviceService);
    public isMobile = this.deviceService.getIsMobile();

    private readonly getVisualizationDataController = inject(
        GetVisualizationDataController
    );

    public expandedRows = new Map<string, boolean>();

    public readonly table = computed((): RowsType[] => {
        if (!this.options) return [];
        const { dates, series, points } =
            this.getVisualizationDataController.execute({
                company: this.company,
                options: this.options,
                visualization: this.visualization
            });
        if (this.options.budget) {
            const rows = this.getRowsBudget(dates, series, points);
            return rows;
        } else if (this.options.comparation.allMonths) {
            const rows = this.getRowsComparationAllMonths(series, points);
            return rows;
        } else if (this.options.comparation.active) {
            const rows = this.getRowsComparation(series, points);
            return rows;
        } else {
            const rows = this.getRowsDefault(dates, series, points);
            return rows;
        }
    });

    private getRowsDefault(
        dates: Date[],
        series: SerieEntity[],
        points: PointEntity[]
    ): RowsType[] {
        const rows: RowsType[] = [];
        dates.reverse().forEach((date, index) => {
            const dateString = DateUtils.formatByPeriodName(
                this.options.period,
                date,
                false
            );

            if (series.length > 1) {
                this.expandedRows.set(dateString, index === 0);
            }
            const row = {
                date: dateString,
                childrens: [] as ChildrensType[]
            };
            series.forEach((serie) => {
                const point = points.find(
                    (p) =>
                        p.t.getTime() === date.getTime() &&
                        p.props.s === serie.props._id
                );
                const value = FormatNumbers.byUnit(
                    serie.props.unit,
                    point ? point.props.m.v : 0,
                    2
                );
                const serieOptions =
                    this.visualization.props.chart?.series.find(
                        (s: any) => s._id === serie.props._id
                    );
                const results: ResultsType[] = [
                    {
                        color: ColorsService.getColor(
                            serieOptions?.color || "petroleum-medium"
                        ),
                        label: point?.props?.p ? "Projeção" : "Resultado",
                        value,
                        isProjection: point?.props?.p
                    }
                ];
                row.childrens.push({
                    serie,
                    results
                });
            });
            rows.push(row);
        });
        return rows;
    }

    private getRowsComparation(
        series: SerieEntity[],
        points: PointEntity[]
    ): RowsType[] {
        const basePeriod = DateUtils.formatByPeriodName(
            this.options.period,
            this.options.endDate,
            false
        );
        const comparationPeriod = DateUtils.formatByPeriodName(
            this.options.comparation.period,
            this.options.comparation.date,
            false
        );
        const dateString = `${basePeriod} x ${comparationPeriod}`;
        this.expandedRows.set(dateString, true);
        const row = {
            date: dateString,
            childrens: [] as ChildrensType[]
        };
        series.forEach((serie) => {
            const basePoint = points.find(
                (p) =>
                    p.t.getTime() === this.options.endDate.getTime() &&
                    p.props.s === serie.props._id &&
                    p.props.sc === this.options?.scenario?.props?._id
            );
            const comparationPoint = points.find(
                (p) =>
                    p.t.getTime() === this.options.comparation.date.getTime() &&
                    p.props.s === serie.props._id &&
                    p.props.sc ===
                        this.options.comparation?.scenario?.props?._id
            );
            const baseValue = FormatNumbers.byUnit(
                serie.props.unit,
                basePoint ? basePoint.props.m.v : 0,
                2
            );
            const comparationValue = FormatNumbers.byUnit(
                serie.props.unit,
                comparationPoint ? comparationPoint.props.m.v : 0,
                2
            );
            const serieOptions = this.visualization.props.chart?.series.find(
                (s: any) => s._id === serie.props._id
            );
            const results: ResultsType[] = [
                {
                    color: ColorsService.getColor(
                        serieOptions?.color || "petroleum-medium"
                    ),
                    label: "Resultado",
                    value: baseValue
                },
                {
                    color: ColorsService.getColor(
                        serieOptions?.color || "petroleum-medium"
                    ),
                    label: `Comparado (${comparationPeriod})`,
                    value: comparationValue,
                    isComparation: true
                }
            ];
            row.childrens.push({
                serie,
                results
            });
        });
        return [row];
    }

    private getRowsBudget(
        dates: Date[],
        series: SerieEntity[],
        points: PointEntity[]
    ): RowsType[] {
        const rows: RowsType[] = [];
        dates.reverse().forEach((date) => {
            const dateString = DateUtils.formatByPeriodName(
                this.options.period,
                date,
                false
            );

            this.expandedRows.set(dateString, true);

            const row = {
                date: dateString,
                childrens: [] as ChildrensType[]
            };
            series.forEach((serie) => {
                const pointPeriod = points.find(
                    (p) =>
                        p.t.getTime() === date.getTime() &&
                        p.props.s === serie.props._id &&
                        p.props.sc === this.options.scenario.props._id
                );
                const pointBudget = points.find(
                    (p) =>
                        p.t.getTime() === date.getTime() &&
                        p.props.s === serie.props._id &&
                        p.props.sc === this.options.budget
                );
                const valuePeriod = FormatNumbers.byUnit(
                    serie.props.unit,
                    pointPeriod ? pointPeriod.props.m.v : 0,
                    2
                );
                const valueBudget = FormatNumbers.byUnit(
                    serie.props.unit,
                    pointBudget ? pointBudget.props.m.v : 0,
                    2
                );
                const serieOptions =
                    this.visualization.props.chart?.series.find(
                        (s: any) => s._id === serie.props._id
                    );
                const results: ResultsType[] = [
                    {
                        color: ColorsService.getColor(
                            serieOptions?.color || "petroleum-medium"
                        ),
                        label: "Resultado",
                        value: valuePeriod
                    },
                    {
                        color: ColorsService.getColor(
                            serieOptions?.color || "petroleum-medium"
                        ),
                        label: `Orçamento (${dateString})`,
                        value: valueBudget,
                        isBudget: true
                    }
                ];
                row.childrens.push({
                    serie,
                    results
                });
            });
            rows.push(row);
        });
        return rows;
    }

    private getRowsComparationAllMonths(
        series: SerieEntity[],
        points: PointEntity[]
    ): RowsType[] {
        const datesBase = DateUtils.getAllMonthsOfYears([
            this.options.endDate.getUTCFullYear()
        ]);
        const datesComparation = DateUtils.getAllMonthsOfYears([
            this.options.comparation.date.getUTCFullYear()
        ]);
        const dates = datesBase
            .map((base, index) => ({
                base,
                comparation: datesComparation[index]
            }))
            .sort((a, b) => b.base.getTime() - a.base.getTime());
        const rows: RowsType[] = [];
        dates.forEach((date) => {
            const basePeriod = DateUtils.formatByPeriodName(
                this.options.period,
                date.base,
                false
            );
            const comparationPeriod = DateUtils.formatByPeriodName(
                this.options.comparation.period,
                date.comparation,
                false
            );
            const dateString = `${basePeriod} x ${comparationPeriod}`;
            if (series.length > 1) {
                this.expandedRows.set(dateString, true);
            }
            const row = {
                date: dateString,
                childrens: [] as ChildrensType[]
            };
            series.forEach((serie) => {
                const basePoint = points.find(
                    (p) =>
                        p.t.getTime() === date.base.getTime() &&
                        p.props.s === serie.props._id &&
                        p.props.sc === this.options?.scenario?.props?._id
                );
                const comparationPoint = points.find(
                    (p) =>
                        p.t.getTime() === date.comparation.getTime() &&
                        p.props.s === serie.props._id &&
                        p.props.sc ===
                            this.options.comparation?.scenario?.props?._id
                );

                const baseValue = FormatNumbers.byUnit(
                    serie.props.unit,
                    basePoint ? basePoint.props.m.v : 0,
                    2
                );
                const comparationValue = FormatNumbers.byUnit(
                    serie.props.unit,
                    comparationPoint ? comparationPoint.props.m.v : 0,
                    2
                );
                const serieOptions =
                    this.visualization.props.chart?.series.find(
                        (s: any) => s._id === serie.props._id
                    );
                const results: ResultsType[] = [
                    {
                        color: ColorsService.getColor(
                            serieOptions?.color || "petroleum-medium"
                        ),
                        label: "Resultado",
                        value: baseValue
                    },
                    {
                        color: ColorsService.getColor(
                            serieOptions?.color || "petroleum-medium"
                        ),
                        label: `Comparado (${comparationPeriod})`,
                        value: comparationValue,
                        isComparation: true
                    }
                ];
                row.childrens.push({
                    serie,
                    results
                });
            });
            rows.push(row);
        });
        return rows;
    }

    public toggleRow(row: RowsType): void {
        if (row.childrens.length <= 1 && row.childrens[0].results.length <= 1)
            return;
        const currentState = this.expandedRows.get(row.date) || false;
        this.expandedRows.set(row.date, !currentState);
        if (!currentState) {
            setTimeout(() => {
                const container = this.elementRef.nativeElement.querySelector(
                    ".table-values-component"
                );
                const element = container.querySelector(
                    `[data-date="${row.date}"] .table-values-component__card-content`
                );
                if (container && element) {
                    const containerRect = container.getBoundingClientRect();
                    const elementRect = element.getBoundingClientRect();
                    container.scrollBy({
                        top: elementRect.top - containerRect.top - 46,
                        behavior: "smooth"
                    });
                }
            }, 100);
        }
    }
}

type RowsType = {
    date: string;
    childrens: ChildrensType[];
};

type ChildrensType = {
    serie: SerieEntity;
    results: ResultsType[];
};

type ResultsType = {
    color: string;
    label: string;
    value: string;
    isComparation?: boolean;
    isBudget?: boolean;
    isProjection?: boolean;
};
